import { Flatfile } from '@flatfile/api';
import { CustomField } from 'types/domain';

const FIELD_TYPE = {
    string: 'string' as const,
    number: 'number' as const,
    enum: 'enum' as const,
    date: 'date' as const,
};

const DATE_DESCRIPTION = `Format: \`MM/DD/YYYY\` or \`YYYY-MM-DD\` (e.g. \`04/17/1991\` or \`1991-04-17\`)`;

const DATERANGE_DESCRIPTION = `
Separate the start and end date with a comma ("start,end").
\r\n &nbsp; \r\n
Example: \`2023-01-01,2023-01-31\`
`;

const DATETIME_DESCRIPTION = `
For non-UTC timezones, the timezone/offset must be included in the timestamp (e.g.: \`12:00:00 EST\` or \`12:00:00-05:00\`).
\r\n &nbsp; \r\n
Must be a valid date format. Examples:
* \`01/01/2023\`
* \`2023-01-01\`
* \`2023-01-01 12:00:00-05:00\`
* \`2023-01-01 12:00:00 EST\`
`;

const CHECKBOX_DESCRIPTION = `
To toggle on: \`true\`, \`1\`, \`yes\`, \`on\`
To toggle off: \`false\`, \`0\`, \`no\`, \`off\`
\r\n &nbsp; \r\n
Leave blank to ignore.
`;

const REQUIRED_CHECKBOX_DESCRIPTION = `
To toggle on: \`true\`, \`1\`, \`yes\`, \`on\`
To toggle off: \`false\`, \`0\`, \`no\`, \`off\`
`;

const MULTISELECT_DESCRIPTION = `
Multiple answers must be separated by a vertical bar (|).
\r\n &nbsp; \r\n
Example: "answer_1|answer_2|answer_3"
`;

const SOCIAL_ACCOUNT_DESCRIPTION =
    'Can provide the full URL (`https://x.com/givebutter`) or only the username/handle (`givebutter`).';

const customFieldsToFlatfileFields = (customFields: CustomField[]): Flatfile.Property[] =>
    customFields
        .filter(field => field.type !== 'file')
        .map(field => {
            switch (field.type) {
                case 'number':
                case 'percentage':
                    return {
                        label: field.title,
                        key: `custom_fields_field_${field.id}`,
                        ...getCustomFieldDescription(field),
                        type: FIELD_TYPE.number,
                    } as Flatfile.Property.Number;
                case 'date':
                    return {
                        label: field.title,
                        key: `custom_fields_field_${field.id}`,
                        ...getCustomFieldDescription(field),
                        type: FIELD_TYPE.date,
                    } as Flatfile.Property.Date_;
                case 'select':
                    return {
                        label: field.title,
                        key: `custom_fields_field_${field.id}`,
                        ...getCustomFieldDescription(field),
                        type: FIELD_TYPE.enum,
                        config: {
                            options: field.answers!.map(answer => ({
                                value: answer,
                                label: answer,
                            })),
                        },
                    } as Flatfile.Property.Enum;
                default:
                    return {
                        label: field.title,
                        key: `custom_fields_field_${field.id}`,
                        ...getCustomFieldDescription(field),
                        type: FIELD_TYPE.string,
                    } as Flatfile.Property.String;
            }
        });

const getCustomFieldDescription = (customField: CustomField) => {
    switch (customField.type) {
        case 'checkbox':
            return {
                description: customField.description
                    ? customField.description + '\r\n &nbsp; \r\n' + CHECKBOX_DESCRIPTION
                    : CHECKBOX_DESCRIPTION,
            };
        case 'date':
            return {
                description: customField.description
                    ? customField.description + '\r\n &nbsp; \r\n' + DATE_DESCRIPTION
                    : DATE_DESCRIPTION,
            };
        case 'daterange':
            return {
                description: customField.description
                    ? customField.description + '\r\n &nbsp; \r\n' + DATERANGE_DESCRIPTION
                    : DATERANGE_DESCRIPTION,
            };
        case 'multiselect':
            return {
                description: customField.description
                    ? customField.description + '\r\n &nbsp; \r\n' + MULTISELECT_DESCRIPTION
                    : MULTISELECT_DESCRIPTION,
            };
        default:
            return customField.description ? { description: customField.description } : {};
    }
};

const submitAction = {
    operation: 'submitActionFg',
    mode: 'foreground' as const,
    label: 'Submit',
    description: 'Submit your imported data.',
    primary: true,
};

export {
    FIELD_TYPE,
    DATE_DESCRIPTION,
    DATERANGE_DESCRIPTION,
    DATETIME_DESCRIPTION,
    CHECKBOX_DESCRIPTION,
    REQUIRED_CHECKBOX_DESCRIPTION,
    MULTISELECT_DESCRIPTION,
    SOCIAL_ACCOUNT_DESCRIPTION,
    customFieldsToFlatfileFields,
    submitAction,
};
